import {HTMLProps, InputProps} from './types';

/*
 * Props that exist for a component's own API and must not reach the DOM.
 *
 * This covers the subclasses too, not just BaseInput's own props: every input
 * molecule funnels its props through `getNativeProps`, so a mask option declared
 * on `InputCurrency` or `InputNumber` reaches the `<input>` unless it is named
 * here. Add new custom props to this list — `domProps.spec.tsx` asserts on the
 * rendered attributes, because React reports an unknown prop only once per prop
 * name per process and would otherwise let the second leak of the same name pass.
 */
const CUSTOM_PROP_NAMES = [
  'innerRef',
  'label',
  'testId',
  'loading',
  'theme',
  'iconSrc',
  'customValidator',
  'size',
  'iconPosition',
  'hintText',
  'hasError',
  'design',
  'textAlign',
  'errorMessage',
  'withErrorIcon',
  'isReady',
  'customLabel',
  'onClickIcon',
  'onValidationChange',
  // Mask options owned by the InputCurrency / InputNumber / InputZip molecules.
  'hideSymbol',
  'allowDecimal',
  'decimalsLimit',
  'customNumberMask',
  'maskLength',
  'showFullValue',
] as const;

// Remove all non-native HTML input props & re-format modified ones
export const getNativeProps = (
  props: InputProps,
): HTMLProps<HTMLInputElement> => {
  /*
   * Typed as a record rather than `as any`: the omissions are driven by one
   * list instead of nineteen `delete` statements, and nothing here is silently
   * untyped. `loading` and `optional` are re-emitted as DOM string attributes.
   */
  const nativeProps: Record<string, unknown> = {...props};

  CUSTOM_PROP_NAMES.forEach((name) => delete nativeProps[name]);

  // START: Convert to native formats
  if (typeof props.loading === 'boolean') {
    nativeProps.loading = props.loading.toString();
  }

  if (typeof props.optional === 'boolean') {
    nativeProps.optional = props.optional.toString();
  }
  // END: Convert to native formats

  return nativeProps as HTMLProps<HTMLInputElement>;
};
